import { useState } from "react";

interface Cursor {
  id: number;
  /** Element ro'yxatdan chiqib ketsa, shu o'rindagisi ochiladi. */
  index: number;
  /** Qaysi ro'yxat uchun ochilgani — filtr almashsa kursor eskiradi. */
  listKey: string;
}

export interface QueueCursor<T> {
  /** Panelda ochiq element (yo'q bo'lsa `null` — panel yopiq). */
  current: T | null;
  /**
   * Panelda CHIZILADIGAN element. Panel yopilayotganda `current` allaqachon
   * `null`, lekin yopilish animatsiyasi hali davom etadi — o'sha paytda
   * panel bo'shab qolmasin, oxirgi element ko'rinib tursin.
   */
  shown: T | null;
  /** Joriy sahifadagi o'rni; ochiq element bo'lmasa `-1`. */
  index: number;
  open: (item: T) => void;
  close: () => void;
  /** Chetda `undefined` — tugma o'chiq turadi. */
  previous?: () => void;
  next?: () => void;
  /**
   * Qaror qabul qilingandan keyin chaqiriladi: navbatdagi element ochiladi,
   * oxirgisi bo'lsa — oldingisi, ro'yxatda boshqa hech narsa qolmasa panel
   * yopiladi.
   */
  advance: () => void;
}

/**
 * Moderatsiya navbatining o'ng paneli uchun kursor.
 *
 * Moderator bir elementni hal qiladi va darhol keyingisiga o'tadi — panelni
 * yopib, jadvaldan keyingi qatorni qidirish shart emas. Qaror qabul qilingan
 * element ro'yxatdan chiqib ketadi (holati o'zgaradi), shuning uchun kursor
 * id'dan tashqari O'RINni ham eslaydi: ro'yxat yangilanib id topilmasa, o'sha
 * o'rinda turgan (ya'ni navbatdagi) element ko'rsatiladi.
 *
 * `listKey` — ro'yxatning kaliti (filtr, sahifa). Filtr almashganda eski
 * kursor yangi ro'yxatning tasodifiy qatorini ochib yubormasin.
 */
export function useQueueCursor<T extends { id: number }>(
  rows: readonly T[],
  listKey: string,
): QueueCursor<T> {
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const live = cursor && cursor.listKey === listKey ? cursor : null;

  let index = -1;
  if (live && rows.length > 0) {
    index = rows.findIndex(row => row.id === live.id);
    if (index === -1) index = Math.min(live.index, rows.length - 1);
  }
  const current = index === -1 ? null : (rows[index] ?? null);

  // Oldingi renderdan qolgan qiymatni yangilash — effekt emas, render
  // paytidagi shartli yozuv (React hujjatidagi "prop o'zgarganda holatni
  // moslash" naqshi): effekt bir kadr kechikib, bo'sh panelni chizardi.
  const [lastShown, setLastShown] = useState<T | null>(null);
  if (current !== null && current !== lastShown) setLastShown(current);

  const go = (target: number) => {
    const row = rows[target];
    setCursor(row ? { id: row.id, index: target, listKey } : null);
  };

  return {
    current,
    shown: current ?? lastShown,
    index,
    open: item => {
      const target = rows.findIndex(row => row.id === item.id);
      setCursor({ id: item.id, index: Math.max(target, 0), listKey });
    },
    close: () => setCursor(null),
    previous: index > 0 ? () => go(index - 1) : undefined,
    next: index !== -1 && index < rows.length - 1 ? () => go(index + 1) : undefined,
    advance: () => {
      if (index === -1) return;
      if (index < rows.length - 1) {
        // Hal qilingan element ro'yxatdan chiqqach, keyingisi aynan shu
        // o'ringa suriladi — shuning uchun o'rin o'zgarmaydi.
        const next = rows[index + 1];
        setCursor(next ? { id: next.id, index, listKey } : null);
      } else if (index > 0) go(index - 1);
      else setCursor(null);
    },
  };
}
